<script setup>
import { computed, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { ChevronLeftIcon, ChevronRightIcon, CurrencyPoundIcon } from '@heroicons/vue/24/solid'
import CalendarMonth from '@/components/CalendarMonth.vue'
import CalendarHeader from '@/components/CalendarHeader.vue'
import { useCalendarStore } from '@/stores/CalendarStore'
import { CALENDAR_VIEW_SETTING_KEY, getAppSetting } from '@/composables/useAppSettingsCookie'

const calendarStore = useCalendarStore()
const route = useRoute()
const router = useRouter()

const getSavedView = () => {
  const storedView = getAppSetting(CALENDAR_VIEW_SETTING_KEY, 'month')
  return storedView === 'day' ? 'day' : 'month'
}
const getQueryView = () => (route.query.view === 'day' || route.query.view === 'month' ? route.query.view : null)
const activeView = computed(() => getQueryView() ?? getSavedView())
const isDayView = computed(() => activeView.value === 'day')
const miniWeekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const getCurrentDate = () => dayjs(calendarStore.getDate).format('YYYY-MM-DD')

const navigateToDate = dateValue => {
  const parsedDate = dayjs(dateValue)
  if (!parsedDate.isValid()) {
    return
  }

  router.replace({
    path: '/calendar',
    query: {
      ...route.query,
      date: parsedDate.format('YYYY-MM-DD'),
      view: 'day',
    },
  })
}

const syncDateFromRoute = () => {
  const rawDate = typeof route.query.date === 'string' ? route.query.date : null
  if (!rawDate) {
    return
  }

  const nextDate = dayjs(rawDate)
  if (!nextDate.isValid()) {
    return
  }

  if (!dayjs(calendarStore.getDate).isSame(nextDate, 'day')) {
    calendarStore.setDate(nextDate)
  }
}

const dayEntries = computed(() => {
  const targetDate = getCurrentDate()
  const filtered = calendarStore.getEntries.filter(entry =>
    (entry.dates ?? []).some(date => dayjs(date.date).format('YYYY-MM-DD') === targetDate),
  )

  return filtered.sort((a, b) => {
    const nameA = (a.reference?.name ?? '').toLowerCase()
    const nameB = (b.reference?.name ?? '').toLowerCase()
    return nameA.localeCompare(nameB)
  })
})

const getEntryClass = entry => {
  const prefix = entry?.reference?.type?.prefix

  if (prefix === 'M1E') return 'label-event'
  if (prefix === 'M1P') return 'label-transport'
  if (prefix === 'M1S') return 'label-365'
  if (prefix === 'M1SJ') return 'label-365j'
  if (prefix === 'M1T') return 'label-training'

  return 'label-clikable bg-teal-500 rounded-md'
}

const isInvoicePaid = entry =>
  entry?.payment_status === true || entry?.payment_status === 1 || entry?.payment_status === '1'

const paidIconClass = 'ml-auto h-5 w-5 shrink-0 rounded-full bg-white/95 p-0.5 text-emerald-700 shadow-sm ring-1 ring-white/70'

const miniMonthDays = computed(() => {
  const selectedDate = dayjs(calendarStore.getDate)
  const monthStart = selectedDate.startOf('month')
  const offset = (monthStart.day() + 6) % 7 // Monday as first day
  const gridStart = monthStart.subtract(offset, 'day')

  return Array.from({ length: 42 }, (_, index) => gridStart.add(index, 'day'))
})

const selectedMonthKey = computed(() => dayjs(calendarStore.getDate).format('YYYY-MM'))

const dayEntryCountMap = computed(() => {
  const uniquePerDay = {}

  calendarStore.getEntries.forEach(entry => {
    (entry.dates ?? []).forEach(date => {
      const key = dayjs(date.date).format('YYYY-MM-DD')
      if (!uniquePerDay[key]) {
        uniquePerDay[key] = new Set()
      }
      uniquePerDay[key].add(entry.id)
    })
  })

  const counts = {}
  Object.keys(uniquePerDay).forEach(key => {
    counts[key] = uniquePerDay[key].size
  })

  return counts
})

const isToday = day => dayjs(day).isSame(dayjs(), 'day')
const isMiniDaySelected = day => dayjs(day).isSame(dayjs(calendarStore.getDate), 'day')
const isMiniDayInCurrentMonth = day => dayjs(day).format('YYYY-MM') === selectedMonthKey.value
const getMiniDayCount = day => dayEntryCountMap.value[dayjs(day).format('YYYY-MM-DD')] ?? 0
const isTodayMonthVisible = computed(() =>
  dayjs(calendarStore.getDate).isSame(dayjs(), 'month'),
)

watch(
  () => route.query.date,
  () => {
    syncDateFromRoute()
  },
)

watch(
  isDayView,
  value => {
    if (value) {
      nextTick(() => {
        calendarStore.startAutoRefresh()
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  syncDateFromRoute()
})

onUnmounted(() => {
  calendarStore.stopAutoRefresh()
})
</script>
<template>
  <div id="calendar" class="h-full min-h-0 box overflow-y-auto">
    <CalendarMonth v-if="!isDayView" />

    <div v-else class="flex h-full min-h-0 flex-col gap-4 px-3 pb-3 md:px-0">
      <CalendarHeader class="w-full min-h-0" view="day" />

      <div class="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div class="mb-3 flex items-center justify-between gap-2">
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Jump to day
            </p>
            <p class="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {{ dayjs(calendarStore.getDate).format('MMMM YYYY') }}
            </p>
          </div>

          <div class="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 p-1 dark:border-slate-600 dark:bg-slate-700/60">
            <button
              type="button"
              class="inline-flex h-8 items-center justify-center gap-1 rounded-full px-2 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-600 dark:hover:text-white"
              @click="navigateToDate(dayjs(calendarStore.getDate).subtract(1, 'month'))"
            >
              <ChevronLeftIcon class="h-4 w-4" />
              <span class="hidden text-[11px] font-semibold sm:inline">Month</span>
            </button>
            <button
              type="button"
              class="rounded-full px-3 py-1 text-left text-xs font-semibold transition"
              :class="isTodayMonthVisible
                ? 'text-slate-700 hover:bg-slate-200 dark:text-slate-100 dark:hover:bg-slate-600'
                : 'bg-cyan-600 text-white shadow-sm hover:bg-cyan-500 dark:bg-cyan-500 dark:text-slate-900 dark:hover:bg-cyan-400'"
              @click="navigateToDate(dayjs())"
            >
              <span class="block leading-none">Today</span>
              <span class="mt-0.5 block text-[10px] leading-none opacity-80">
                Jump to current month
              </span>
            </button>
            <button
              type="button"
              class="inline-flex h-8 items-center justify-center gap-1 rounded-full px-2 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-600 dark:hover:text-white"
              @click="navigateToDate(dayjs(calendarStore.getDate).add(1, 'month'))"
            >
              <span class="hidden text-[11px] font-semibold sm:inline">Month</span>
              <ChevronRightIcon class="h-4 w-4" />
            </button>
          </div>
        </div>

        <div class="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
          <span v-for="label in miniWeekdayLabels" :key="label">{{ label }}</span>
        </div>

        <div class="mt-1 grid grid-cols-7 gap-1">
          <button
            v-for="day in miniMonthDays"
            :key="day.format('YYYY-MM-DD')"
            type="button"
            class="relative h-8 rounded-lg text-xs font-semibold transition sm:h-9"
            :class="[
              isMiniDaySelected(day)
                ? 'bg-slate-900 text-white shadow-sm dark:bg-slate-100 dark:text-slate-900'
                : isMiniDayInCurrentMonth(day)
                  ? 'text-slate-700 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700'
                  : 'text-slate-400 hover:bg-slate-100/80 dark:text-slate-500 dark:hover:bg-slate-700/60',
              isToday(day) && !isMiniDaySelected(day) ? 'ring-1 ring-cyan-500/60' : '',
            ]"
            @click="navigateToDate(day)"
          >
            {{ day.format('D') }}
            <span
              v-if="getMiniDayCount(day)"
              class="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
              :class="isMiniDaySelected(day) ? 'bg-white/80 dark:bg-slate-800' : 'bg-cyan-500'"
            ></span>
          </button>
        </div>
      </div>

      <div v-if="dayEntries.length" class="flex-1 min-h-0 overflow-y-auto space-y-3">
        <router-link
          v-for="entry in dayEntries"
          :key="entry.id"
          :to="'/reference/' + entry.reference.id"
          class="block"
        >
          <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md dark:border-slate-700 dark:bg-slate-800">
            <div class="mb-3 flex items-center gap-2">
              <span
                :class="[
                  'inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs font-semibold',
                  getEntryClass(entry),
                ]"
              >
                {{ entry.reference.txt }}
              </span>
              <span class="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                {{ entry.reference.name }}
              </span>
              <CurrencyPoundIcon
                v-show="isInvoicePaid(entry)"
                :class="paidIconClass"
              />
            </div>
            <p class="text-sm text-slate-600 dark:text-slate-300">
              {{ entry.reference.description || 'No description' }}
            </p>
          </div>
        </router-link>
      </div>

      <div v-else class="rounded-xl border border-dashed border-slate-300 bg-white/70 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
        There are no calendar entries to display.
      </div>
    </div>
  </div>
</template>
